import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { useAuditTimeZone } from "../../hooks/useAuditTimeZone";
import { DriverLabel } from "../../hooks/useDriverLabels";
import { AuditRow, DaySummaryRow } from "../../models/AuditRow";
import { AuditView } from "../../models/AuditView";
import { AUDIT_TOKENS } from "../../theme/auditTokens";
import { formatConsumption, formatDistance, formatDuration, formatNumber } from "../../utils/format";
import { formatClockInZone, formatDayInZone } from "../../utils/timeZone";
import { AuditButton } from "../common/AuditButton";
import { StatusPill } from "../common/StatusPill";
import { SurfaceCard } from "../common/SurfaceCard";
import { AuditNoRowsOverlay, EmptyReason } from "../grid/AuditNoRowsOverlay";
import { LoadMoreBar, LoadMoreProps } from "../grid/LoadMoreBar";
import { rowReasonText } from "../grid/rowValues";
import { VIEW_TONES } from "../header/ViewSwitch";

interface Props {
  rows: AuditRow[];
  view: AuditView;
  distanceDecimals: number;
  driverLabel: DriverLabel;
  onShowMap: (row: AuditRow) => void;
  loadMore: LoadMoreProps;
  emptyReason: EmptyReason;
}

/**
 * Below 820px the table gives way to one card per vehicle and day (spec section 8). The analog
 * cards show the drivers' entries only, the digital cards Geotab's figures and the map.
 */
export function DayCardList({ rows, view, distanceDecimals, driverLabel, onShowMap, loadMore, emptyReason }: Props) {
  const { t, i18n } = useTranslation();
  const timeZone = useAuditTimeZone();
  const days = rows.filter((row): row is DaySummaryRow => row.rowType === "day");
  const analog = view === "analog";

  if (days.length === 0) {
    return (
      <SurfaceCard>
        <AuditNoRowsOverlay emptyReason={emptyReason} />
        {/* The loaded vehicles may have no trips while others do: keep "load more" reachable, as the desktop grid does. */}
        {emptyReason !== "loading" && emptyReason !== "loadError" && <LoadMoreBar {...loadMore} />}
      </SurfaceCard>
    );
  }

  const label = (key: string) => (
    <Box sx={{ fontSize: 10.5, letterSpacing: ".06em", textTransform: "uppercase", color: AUDIT_TOKENS.muted, fontWeight: 600, mb: "3px" }}>
      {t(`logbookAudit.columns.${key}`)}
    </Box>
  );
  const value = (text: string) => <Box sx={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>{text || "—"}</Box>;
  const kilometres = (km?: number) => (km == null ? "" : `${formatNumber(km, i18n.language, analog ? 0 : 1)} km`);
  const litres = (l?: number) => (l == null ? "" : `${formatNumber(l, i18n.language, 1)} l`);
  // Fuel of an electric vehicle is what Geotab's charging detection reports, in kWh.
  const refuelled = (value: number | undefined, day: DaySummaryRow) =>
    day.device.fuelType === "electric" ? formatConsumption(value, "electric", i18n.language) : litres(value);
  const clock = (date?: Date) => (date ? formatClockInZone(date, timeZone) : "");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {days.map(day => {
        return (
          <SurfaceCard key={day.id} as="article" sx={{ p: "14px", boxShadow: `inset 4px 0 0 ${VIEW_TONES[view].fg}` }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "flex-start" }}>
              <Box>
                <Box sx={{ fontWeight: 700, color: AUDIT_TOKENS.ink, fontSize: 15, lineHeight: 1.3 }}>{day.device.name}</Box>
                <Box sx={{ fontSize: 12, color: AUDIT_TOKENS.muted, mt: "4px" }}>
                  {formatDayInZone(day.date, timeZone)} · {t("logbookAudit.mobile.tripCount", { count: day.trips.length })}
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: "4px" }}>
                {day.recordTypes.map(recordType => (
                  <StatusPill key={recordType} tone={recordType}>
                    {t(`logbookAudit.recordType.${recordType}`)}
                  </StatusPill>
                ))}
              </Box>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "10px 14px", mt: "12px", pt: "12px", borderTop: `1px solid ${AUDIT_TOKENS.line}` }}>
              <div>{label("driver")}{value(day.driverIds.map(driverLabel).join(", "))}</div>
              {analog && <div>{label("dutyRoster")}{value(day.dutyRosterNumbers.join(", "))}</div>}
              <div>{label("firstStart")}{value(clock(day.firstStart))}</div>
              <div>{label("lastStop")}{value(clock(day.lastStop))}</div>
              <div>{label("odometerStart")}{value(kilometres(day.odometerStartKm))}</div>
              <div>{label("odometerEnd")}{value(kilometres(day.odometerEndKm))}</div>
              <div>{label("duration")}{value(formatDuration(day.durationMs))}</div>
              <div>{label("distance")}{value(day.distance == null ? "" : formatDistance(day.distance, i18n.language, analog ? 0 : distanceDecimals))}</div>
              {!analog && <div>{label("fuelType")}{value(day.device.fuelType ? t(`logbookAudit.fuelType.${day.device.fuelType}`) : "")}</div>}
              {!analog && <div>{label("consumption")}{value(formatConsumption(day.consumption, day.device.fuelType, i18n.language))}</div>}
              <div>{label("refuel")}{value(refuelled(day.refuelLiters, day))}</div>
              {day.adBlueLiters != null && <div>{label("adBlue")}{value(litres(day.adBlueLiters))}</div>}
              <div>{label("reason")}{value(rowReasonText(day))}</div>
            </Box>
            {!analog && (
              <Box sx={{ mt: "12px", pt: "10px", borderTop: `1px solid ${AUDIT_TOKENS.line}` }}>
                <AuditButton onClick={() => onShowMap(day)}>{t("logbookAudit.map.showDay")}</AuditButton>
              </Box>
            )}
          </SurfaceCard>
        );
      })}
      <SurfaceCard>
        <LoadMoreBar {...loadMore} />
      </SurfaceCard>
    </Box>
  );
}
